/**
 * Search — §9, the screen.
 *
 * Three things about this page are load-bearing and none of them is visible:
 *
 * **It never renders a denial.** §9.2 joins `permission_visible_nodes` inside
 * the one statement, so an unreachable document is not refused — it is never a
 * row. There is no 403 branch here because the API cannot produce one, and a
 * 403 would itself confirm that something matched.
 *
 * **It never removes a row.** The count beside the results came back from the
 * same statement as the page (§9.2), so anything dropped here would make the
 * page shorter than its own count — the pagination gap that leaks the existence
 * of documents. The only rearranging below is a `slice` at a boundary the API
 * already sorted on, and `matched.length + similar.length` is the whole page by
 * construction.
 *
 * **Nothing selected is not reachable.** `kinds: []` serialises to an empty
 * string, which is dropped from the query string, which the route reads as "no
 * filter" and therefore *all* kinds — so unchecking every box would widen the
 * search instead of emptying it (see the note on `SearchFilters` in
 * `api/queries.ts`). The kind filter is a toggle group with
 * `disallowEmptySelection`, which makes the state that lies structurally
 * unreachable rather than handled.
 *
 * **URL state.** The page seeds itself from `window.location.search` on first
 * render and then keeps its filters in component state, because `/search` is
 * not in the route tree yet and a `validateSearch` schema is the router's to
 * declare. `state` / `onStateChange` are the seam for when it is: pass them and
 * this component becomes controlled, and the URL follows every keystroke.
 */

import { useEffect, useMemo, useState } from 'react';
import { ToggleButton, ToggleButtonGroup } from 'react-aria-components';
import { useSearch as useSearchResults, useSpaces } from '../../api/queries.ts';
import { NODE_TYPES, type SearchKind } from '../../api/types.ts';
import { Button } from '../../components/ui/button.tsx';
import { cn, FOCUS_RING } from '../../components/ui/cn.ts';
import { EmptyState, ErrorState, Loading } from '../../components/ui/feedback.tsx';
import { Select, TextField } from '../../components/ui/field.tsx';
import { Card, PageHeader } from '../../components/ui/layout.tsx';
import { SearchHitRow, type SpaceNames } from './results.tsx';

/**
 * Everything the URL would carry. Named the way the API names it — `q`, `kind`,
 * `spaceId`, `includeArchived` — so that a route schema, this state, and the
 * query string are one vocabulary rather than three.
 */
export interface SearchPageState {
  q: string;
  /** Never empty; see the note at the top of the file. */
  kinds: readonly SearchKind[];
  spaceId: string | undefined;
  includeArchived: boolean;
  offset: number;
}

/**
 * Smaller than the API's default of 50. A search result is read, not scanned:
 * twenty-five document snippets is already a long page, and the count above
 * them says how much more there is.
 */
const PAGE_SIZE = 25;

export const DEFAULT_SEARCH_STATE: SearchPageState = {
  q: '',
  kinds: NODE_TYPES,
  spaceId: undefined,
  includeArchived: false,
  offset: 0,
};

const KIND_LABELS: Record<SearchKind, string> = {
  document: 'Documents',
  file: 'Files',
  link: 'Links',
};

function isSearchKind(value: string): value is SearchKind {
  return (NODE_TYPES as readonly string[]).includes(value);
}

/**
 * A deep link, read once.
 *
 * Plain `URLSearchParams` rather than the router's `useSearch`, which needs a
 * `validateSearch` on a route that does not exist yet. Reading is the half that
 * matters for a link somebody pasted into Slack; writing is the half that needs
 * the route, and is what `onStateChange` is for.
 */
function readStateFromUrl(): SearchPageState {
  if (typeof window === 'undefined') return DEFAULT_SEARCH_STATE;
  const params = new URLSearchParams(window.location.search);

  const kinds = (params.get('kind') ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter(isSearchKind);
  const offset = Number.parseInt(params.get('offset') ?? '', 10);

  return {
    q: params.get('q') ?? '',
    // An empty or unparseable `kind` is every kind, which is what the route
    // does with an absent one.
    kinds: kinds.length > 0 ? kinds : NODE_TYPES,
    spaceId: params.get('spaceId') ?? undefined,
    // The API spells this as the two literals rather than coercing, so
    // `includeArchived=1` is a 400 there and is `false` here.
    includeArchived: params.get('includeArchived') === 'true',
    offset: Number.isFinite(offset) && offset > 0 ? offset : 0,
  };
}

/**
 * The term, settled.
 *
 * Every keystroke would otherwise be a full-text query plus a trigram scan over
 * every title the person can reach. `keepPreviousData` in `searchQuery` already
 * keeps the list from collapsing to a spinner between renders; this keeps the
 * database out of the loop until the typing stops. Filters are *not* debounced
 * — a click is a decision, and waiting a fifth of a second to honour one reads
 * as a bug.
 */
function useDebounced<T>(value: T, delay: number): T {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);

  return settled;
}

const TOGGLE =
  'inline-flex h-9 cursor-pointer select-none items-center rounded-lg border border-primary ' +
  'bg-primary px-3 text-sm font-medium text-secondary transition-colors ' +
  'data-hovered:bg-primary-hover ' +
  'data-selected:border-brand data-selected:bg-brand-primary data-selected:text-brand-secondary ' +
  'data-disabled:cursor-not-allowed data-disabled:bg-disabled data-disabled:text-disabled';

export interface SearchPageProps {
  /** Router-owned state, once `/search` declares a `validateSearch`. */
  state?: SearchPageState;
  onStateChange?: (next: SearchPageState) => void;
}

export function SearchPage({ state, onStateChange }: SearchPageProps = {}) {
  const [internal, setInternal] = useState<SearchPageState>(readStateFromUrl);
  const current = state ?? internal;

  /**
   * Any change to what is being asked resets paging. Offset is an index into a
   * result set that no longer exists the moment the question changes, and
   * landing on page four of a new search reads as "no results".
   */
  const update = (patch: Partial<SearchPageState>) => {
    const next: SearchPageState = { ...current, offset: 0, ...patch };
    setInternal(next);
    onStateChange?.(next);
  };

  const spaces = useSpaces();
  const spaceNames: SpaceNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const space of spaces.data?.spaces ?? []) names.set(space.id, space.name);
    return names;
  }, [spaces.data]);

  const term = useDebounced(current.q.trim(), 200);

  /**
   * All three kinds and no `kind` at all are the same query, so the filter is
   * sent only when it is actually filtering — which keeps one search from
   * occupying two cache entries depending on how the person arrived at it.
   */
  const kinds = current.kinds.length === NODE_TYPES.length ? undefined : current.kinds;

  const results = useSearchResults({
    q: term,
    kinds,
    spaceId: current.spaceId,
    includeArchived: current.includeArchived,
    limit: PAGE_SIZE,
    offset: current.offset,
  });

  const spaceOptions = [
    { value: 'all', label: 'Every space you can reach' },
    ...(spaces.data?.spaces ?? []).map((space) => ({
      value: space.id,
      label: space.name,
      description: space.kind === 'client' ? 'Client space' : 'Internal space',
    })),
  ];

  const data = results.data;
  const hits = data?.results ?? [];

  /**
   * §9.1's two tiers, split rather than filtered.
   *
   * `matchedText` is false for a row that is here because its *title resembles*
   * what was typed — pg_trgm's typo tolerance — and the statement sorts every
   * true ahead of every false, so the first false is the boundary. Labelling
   * the second group is the difference between "search found something odd" and
   * "search guessed at a spelling"; both slices are rendered, and together they
   * are the page.
   */
  const boundary = hits.findIndex((hit) => !hit.matchedText);
  const matched = boundary === -1 ? hits : hits.slice(0, boundary);
  const similar = boundary === -1 ? [] : hits.slice(boundary);

  const total = data?.total ?? 0;
  const from = total === 0 ? 0 : current.offset + 1;
  const to = Math.min(current.offset + PAGE_SIZE, total);

  return (
    <>
      <PageHeader
        title="Search"
        description="Documents, files, and links, across every space you can reach. The access check is part of the query rather than a filter over its results — an unreachable document is not withheld from this page, it was never a row."
      />

      <Card padded={false}>
        <div className="flex flex-col gap-4 border-b border-secondary p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <TextField
              label="Search"
              type="search"
              className="sm:max-w-md sm:flex-1"
              value={current.q}
              onChange={(value) => update({ q: value })}
              placeholder="Words from a title, a body, a filename, or a URL"
              autoFocus
            />
            <Select
              label="Space"
              className="sm:w-64"
              options={spaceOptions}
              selectedKey={current.spaceId ?? 'all'}
              onSelectionChange={(value) =>
                update({ spaceId: value === 'all' ? undefined : value })
              }
              description="One space exactly, not its subtree."
            />
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <ToggleButtonGroup
              selectionMode="multiple"
              // Empty would mean "all kinds" to the route rather than "none",
              // so the group refuses to get there. Everything the toggles show
              // is therefore what the query says.
              disallowEmptySelection
              selectedKeys={new Set(current.kinds)}
              onSelectionChange={(keys) =>
                // Re-derived from NODE_TYPES rather than from the Set, which
                // both narrows `Key` back to `SearchKind` and fixes the order:
                // the query key hashes arrays positionally, so an unstable
                // order would cache the same search twice.
                update({ kinds: NODE_TYPES.filter((kind) => keys.has(kind)) })
              }
              className="flex flex-wrap items-center gap-2"
              aria-label="Kinds of node to search"
            >
              {NODE_TYPES.map((kind) => (
                <ToggleButton key={kind} id={kind} className={cn(TOGGLE, FOCUS_RING)}>
                  {KIND_LABELS[kind]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <span aria-hidden="true" className="mx-1 h-5 w-px bg-tertiary" />

            <ToggleButton
              isSelected={current.includeArchived}
              onChange={(isSelected) => update({ includeArchived: isSelected })}
              className={cn(TOGGLE, FOCUS_RING)}
            >
              Include archived
            </ToggleButton>
            <span className="text-xs text-tertiary">
              §9.3 — archived documents are excluded unless they are asked for. Drafts are always
              here, for the people who can edit them.
            </span>
          </div>
        </div>

        {term === '' ? (
          <EmptyState
            title="Type something"
            description="Titles are weighted above headings and headings above body text, and a title that merely resembles what you typed still comes back — under its own heading, so a guess never poses as a match."
          />
        ) : results.isError ? (
          <div className="p-5">
            <ErrorState error={results.error} />
          </div>
        ) : results.isPending ? (
          <Loading label="Searching…" />
        ) : total === 0 ? (
          <EmptyState
            title={`Nothing matched “${data?.query ?? term}”`}
            description="No title, body, filename, or URL you can reach contains those words, and nothing was close enough to be a fuzzy title match. Widening the kinds, or including archived documents, is the next thing to try."
          />
        ) : (
          <div className={results.isPlaceholderData ? 'opacity-60 transition-opacity' : undefined}>
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-secondary px-5 py-3">
              <p className="text-sm text-tertiary">
                Showing <span className="font-medium text-secondary">{from}</span>–
                <span className="font-medium text-secondary">{to}</span> of{' '}
                <span className="font-medium text-secondary">{total}</span>
              </p>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  isDisabled={current.offset === 0}
                  onPress={() => update({ offset: Math.max(0, current.offset - PAGE_SIZE) })}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  isDisabled={to >= total}
                  onPress={() => update({ offset: current.offset + PAGE_SIZE })}
                >
                  Next
                </Button>
              </div>
            </div>

            <ul className="flex flex-col">
              {matched.map((hit) => (
                <SearchHitRow key={hit.nodeId} hit={hit} spaceNames={spaceNames} />
              ))}
            </ul>

            {similar.length > 0 ? (
              <>
                <p className="border-y border-secondary bg-secondary px-5 py-2 text-xs font-semibold text-tertiary">
                  Similar titles — nothing in these matched the words, but their titles resemble
                  them
                </p>
                <ul className="flex flex-col">
                  {similar.map((hit) => (
                    <SearchHitRow key={hit.nodeId} hit={hit} spaceNames={spaceNames} />
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        )}
      </Card>
    </>
  );
}
